import * as React from "react";
import { useState } from "react";
import {
  PATIENT_DOMAIN,
  PATIENT_LIST_ENDPOINT,
  patientApi,
  PatientType,
} from "api/patientApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CustomModal from "../common/CustomModal";
import Button from "@mui/joy/Button";
import { theme } from "../../styles/theme";

export default function PatientTaskUpdateRequiredEnableButton({
  patient,
  onAfterAction,
}: {
  patient: PatientType;
  onAfterAction: () => void;
}) {
  const [modalOpen, setModalOpen] = useState(false);

  const {
    patientId,
    isTaskUpdateRequired,
    unreadMessageCount,
    isHumanCheckRequired,
    isRiskCheckRequired,
    isExpirationNoticeRequired,
  } = patient;

  const higherPriorityLabel = React.useMemo(() => {
    if ((unreadMessageCount || 0) > 0) return "채팅 확인 필요";
    if (isHumanCheckRequired) return "휴먼 체크 필요";
    if (isRiskCheckRequired) return "위험 체크 필요";
    if (isExpirationNoticeRequired) return "만료 안내 필요";
    return undefined;
  }, [unreadMessageCount, isHumanCheckRequired, isRiskCheckRequired, isExpirationNoticeRequired]);

  const allowed = React.useMemo(() => {
    return !isTaskUpdateRequired && !higherPriorityLabel;
  }, [isTaskUpdateRequired, higherPriorityLabel]);

  const { modalTitle, modalSubtitle } = React.useMemo(() => {
    if (higherPriorityLabel) {
      return {
        modalTitle: "업데이트 필요 목록에 넣을 수 없어요" as React.ReactNode,
        modalSubtitle: `${higherPriorityLabel} 목록에서 먼저 처리해주세요.` as React.ReactNode,
      };
    }

    if (isTaskUpdateRequired) {
      return {
        modalTitle: "이미 목록에 들어가 있어요" as React.ReactNode,
        modalSubtitle: "업데이트 필요 목록을 확인해보세요." as React.ReactNode,
      };
    }

    return { modalTitle: "", modalSubtitle: "" } as {
      modalTitle: React.ReactNode;
      modalSubtitle: React.ReactNode;
    };
  }, [isTaskUpdateRequired, higherPriorityLabel]);

  const queryClient = useQueryClient();

  const { mutate: enablePatientUpdateRequired } = useMutation(
    () => patientApi.enableTaskUpdateRequired(patientId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PATIENT_DOMAIN, PATIENT_LIST_ENDPOINT]);
      },
    }
  );

  return (
    <>
      <Button
        size="sm"
        variant="Plain"
        color={"neutral"}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          if (!allowed) {
            setModalOpen(true);
            return;
          }
          onAfterAction();
          enablePatientUpdateRequired();
        }}
        sx={{
          width: "100%",
          height: "44px",
          fontSize: "14px",
          fontWeight: 500,
          px: "20px",
          justifyContent: "flex-start",
          whiteSpace: "nowrap",
          color: theme.vars.palette.Content.Strong,
          backgroundColor: theme.vars.palette.Surface.Forward,
        }}
      >
        {"업데이트 필요 목록에 넣기"}
      </Button>
      <CustomModal
        open={modalOpen}
        onClose={() => {
          onAfterAction();
          setModalOpen(false);
        }}
        title={modalTitle}
        subtitle={modalSubtitle}
        actions={[
          {
            label: "확인",
            variant: "Solid",
            onClick: () => {
              if (allowed) {
                enablePatientUpdateRequired();
              }
              onAfterAction();
              setModalOpen(false);
            },
          },
        ]}
      />
    </>
  );
}
